/**
 * settings 도메인 설치자 (S4c) — 설정·치트·QA대시보드·광고 미리보기 모달 조립 + SettingsFlow 결선.
 *
 * 설정/치트는 한 진입 흐름(설정 모달 → 치트/대시보드 버튼)이라 한 설치자로 묶는다.
 * 셸 접점: provideSettingsToggle(HUD 톱니용 진입점) + api{toggleSettings}(dev 도구용).
 * 치트 조립은 CHEAT_ENABLED 상수 게이트 (버튼 노출 단계 차단 — 모달 자체는 dev 빌드에만 의미).
 */
import { Capacitor } from "@capacitor/core";
import { resolveAdPlatform } from "@yjsgames/house-ads";
import { CHEAT_ENABLED } from "../env/appFlavor.js";
import { diag } from "../app/diag.js";
import { t } from "../i18n/i18n.js";
import type { AccountLinkProvider } from "@yjsgames/account-link";
import { buildSupportMail, SUPPORT_EMAIL } from "@yjsgames/support-contact";
import type { DomainBootstrapper, GameBoot } from "./BootTypes.js";
import { AnalyticsEvent } from "../analytics/analyticsEvents.js";
import {
  OTHER_GAMES_SOURCE,
  OTHER_GAMES_TARGET,
  resolveOtherGamesUrl,
} from "../app/otherGamesLinks.js";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "../ui/uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { buildModuleBrickCheatPanel } from "./moduleBrickCheatPanel.js";
import { previewHouseAd } from "./cheatHouseAdPreview.js";
import { resolveContactConfirmCopyKeys } from "./settingsContactLabels.js";
import { isCheatConsoleShortcut } from "./settingsCheatShortcut.js";

const log = diag("settings");

export const settingsBoot: DomainBootstrapper = {
  key: "settings",
  install(boot) {
    const toggle = (): void => toggleSettings(boot);
    boot.provideSettingsToggle(toggle); // HUD 톱니 (게임 뷰 진입점)
    boot.api.register({ toggleSettings: toggle }); // AutoPlay TOGGLE_SETTINGS
    installCheatConsoleShortcut(boot);
  },
};

function installCheatConsoleShortcut(boot: GameBoot): void {
  if (!CHEAT_ENABLED || typeof window === "undefined") return;
  const onKeyDown = (event: KeyboardEvent): void => {
    if (!isCheatConsoleShortcut(event)) return;
    event.preventDefault();
    toggleCheatConsole(boot, event.ctrlKey);
  };
  window.addEventListener("keydown", onKeyDown);
  boot.addDisposer(() => window.removeEventListener("keydown", onKeyDown));
}

function toggleCheatConsole(boot: GameBoot, initialControlHeld = false): void {
  if (boot.modals.cheatOpen) {
    boot.modals.closeCheat();
    return;
  }
  openCheat(boot, { initialControlHeld });
}

/** 설정 모달 토글 (우상단 톱니). 탭 시스템과 독립 — 화면 중앙 dim 모달(ModalLayer). */
function toggleSettings(boot: GameBoot): void {
  const { modals, settings } = boot;
  if (modals.settingsOpen) {
    modals.closeSettings();
    return;
  }
  const screen = boot.screen();
  log.diag("open", `lang=${settings.language} format=${settings.numberFormat}`);
  modals.openSettings({
    screenWidth: screen.width,
    screenHeight: screen.height,
    soundVolumes: settings.soundVolumes,
    version: boot.version,
    language: settings.language,
    numberFormat: settings.numberFormat,
    onChangeSoundVolume: (cat, level) => settings.setSoundVolume(cat, level),
    onSelectLanguage: (code) => settings.setLanguageTo(code),
    onSelectNumberFormat: (format) => settings.setNumberFormatTo(format),
    targetFps: settings.targetFps,
    onSelectTargetFps: (fps) => settings.setTargetFpsTo(fps),
    accountNeedsLink: accountNeedsLink(boot.accountLink()),
    // "계정 연동 및 삭제" — 항상 노출(웹 포함). 모달이 연동(네이티브만) + 삭제(yes/no 확인)를 담는다.
    onOpenAccount: () => openAccountModal(boot),
    onOpenContact: () => openContact(boot),
    onOpenOtherGames: () => openOtherGamesConfirm(boot),
    cheatAvailable: CHEAT_ENABLED,
    onOpenCheat: () => openCheat(boot),
    onOpenDebugDashboard: () => openDebugDashboard(boot),
    onClose: () => modals.closeSettings(),
  });
}

/** "다른 게임 하러 가기" — 외부 스토어 개발자 페이지 이동을 yes/no 확인 후 실행한다. */
function openOtherGamesConfirm(boot: GameBoot): void {
  const screen = boot.screen();
  boot.modals.openConfirm({
    screenWidth: screen.width,
    screenHeight: screen.height,
    title: t("settings.otherGames"),
    message: t("settings.otherGames.confirm"),
    confirmLabel: t("settings.otherGames.go"),
    cancelLabel: t("settings.deleteConfirm.no"),
    onConfirm: () => {
      boot.modals.closeConfirm();
      const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
      const platform = resolveAdPlatform(Capacitor.getPlatform(), userAgent);
      const { store, url } = resolveOtherGamesUrl(platform);
      boot.ctx.analytics().log(AnalyticsEvent.OtherGamesClick, {
        source: OTHER_GAMES_SOURCE,
        store,
        target: OTHER_GAMES_TARGET,
        url,
      });
      if (typeof window !== "undefined") window.open(url, "_blank", "noopener");
    },
    onCancel: () => boot.modals.closeConfirm(),
  });
}

/**
 * "계정 연동 및 삭제" 모달 — 설정 버튼이 띄운다(설정 위 스택). 연동 섹션은 네이티브(provider 있음)일
 * 때만(Android=Play Games / iOS=Game Center), 웹은 삭제 버튼만. 삭제 → yes/no 확인 팝업 경유 resetSave.
 */
function openAccountModal(boot: GameBoot): void {
  const screen = boot.screen();
  const provider = boot.accountLink(); // 웹/로컬단독은 null → 연동 섹션 생략
  boot.modals.openAccountLink({
    screenWidth: screen.width,
    screenHeight: screen.height,
    link: provider?.isSupported() ? linkSection(boot, provider) : null,
    onDeleteAccount: () => openDeleteConfirm(boot),
    onClose: () => boot.modals.closeAccountLink(),
  });
}

/**
 * "문의하기" — 확인 팝업(메일 전송은 비가역) 후 로그 첨부 메일을 native share / 웹 mailto로 띄운다.
 * 디바이스 정보 + 최근 로그는 boot.collectSupportLog()가 조립(LogCollector). 수신 주소·제목은
 * buildSupportMail SSoT. yes에서만 share 실행, no는 팝업만 닫는다.
 */
function openContact(boot: GameBoot): void {
  const screen = boot.screen();
  // 웹은 로그 파일 다운로드만(메일 첨부 자동화 불가) → 다운로드 안내 문구. 네이티브는 share sheet 안내.
  const platform = Capacitor.getPlatform();
  const { messageKey, confirmLabelKey } = resolveContactConfirmCopyKeys(platform);
  boot.modals.openConfirm({
    screenWidth: screen.width,
    screenHeight: screen.height,
    title: t("settings.contact"),
    message: t(messageKey, { email: SUPPORT_EMAIL }),
    confirmLabel: t(confirmLabelKey),
    cancelLabel: t("settings.deleteConfirm.no"),
    onConfirm: () => {
      boot.modals.closeConfirm();
      const req = buildSupportMail({
        version: boot.version,
        platform,
        body: t("settings.contact.body"),
        logContent: boot.collectSupportLog(),
        nowMs: Date.now(),
      });
      boot.supportShare().share(req);
    },
    onCancel: () => boot.modals.closeConfirm(),
  });
}

/** 연동 섹션 — provider.link 결선(성공 시 토스트 + 모달 상태 인플레이스 갱신, 실패 안내, 취소 무동작). */
function linkSection(boot: GameBoot, provider: AccountLinkProvider) {
  return {
    kind: provider.kind,
    isSupported: () => provider.isSupported(),
    status: provider.getStatus(),
    onConnect: (): void => {
      boot.ui.showToast(t("settings.linkAccount.start"));
      try {
        provider.link({
          onSuccess: (status) => {
            boot.ui.showToast(t("settings.linkAccount.success"));
            boot.modals.updateAccountLinkStatus(status);
            boot.modals.setAccountLinkConnecting(false);
            boot.setAccountNeedsLink(!status.linked);
          },
          onCancel: () => {
            boot.modals.setAccountLinkConnecting(false);
          },
          onError: (err) => {
            boot.modals.setAccountLinkConnecting(false);
            if (err.messageKey) boot.ui.showToast(t(err.messageKey));
          },
        });
      } catch {
        boot.modals.setAccountLinkConnecting(false);
        boot.ui.showToast(t("settings.linkAccount.errorGeneric"));
      }
    },
  };
}

function accountNeedsLink(provider: AccountLinkProvider | null): boolean {
  return provider?.isSupported() === true && provider.getStatus().linked === false;
}

/** 계정 삭제 확인 — "정말 삭제? 복구 불가" yes/no. yes에서만 모달들 닫고 resetSave(저장 초기화 + 재시작). */
function openDeleteConfirm(boot: GameBoot): void {
  const screen = boot.screen();
  boot.modals.openConfirm({
    screenWidth: screen.width,
    screenHeight: screen.height,
    title: t("settings.deleteConfirm.title"),
    message: t("settings.deleteConfirm.body"),
    confirmLabel: t("settings.deleteConfirm.yes"),
    cancelLabel: t("settings.deleteConfirm.no"),
    danger: true,
    onConfirm: () => {
      boot.modals.closeConfirm();
      boot.modals.closeAccountLink();
      boot.modals.closeSettings();
      boot.resetSave();
    },
    onCancel: () => boot.modals.closeConfirm(),
  });
}

/** DEV 치트 패널 — 설정 모달을 닫고 Jahro식 비주얼 치트 패널로 전환한다. */
function openCheat(boot: GameBoot, opts: { readonly initialControlHeld?: boolean } = {}): void {
  const { modals } = boot;
  const screen = boot.screen();
  modals.openCheat({
    screenWidth: screen.width,
    screenHeight: screen.height,
    spec: buildModuleBrickCheatPanel(boot),
    panelStyle: CONTENT_PANEL_STYLE,
    buttonStyle: PRIMARY_BUTTON_STYLE,
    fontFamily: FONT_FAMILY,
    fontSize: {
      title: TYPO.title,
      body: TYPO.body,
      caption: TYPO.caption,
    },
    theme: {
      overlayColor: PALETTE_NUM.overlayDim,
      titleColor: PALETTE.accentGold,
      textColor: PALETTE.textPrimary,
      dimTextColor: PALETTE.textDim,
      errorColor: PALETTE.signalDanger,
    },
    feedback: {
      success: ({ command, message }) => boot.ui.showToast(message ?? `${command.label} 실행 완료`),
      error: ({ command }) => boot.ui.showToast(`${command.label} 실행 실패`),
    },
    initialControlHeld: opts.initialControlHeld,
    onClose: () => modals.closeCheat(),
  });
}

/** QA 대시보드 모달 — 스냅샷 표시 + 치트/던전/탭 점프 (CHEAT_ENABLED 게이트는 버튼 노출 단계). */
function openDebugDashboard(boot: GameBoot): void {
  const { modals } = boot;
  const cheats = boot.cheats();
  const screen = boot.screen();
  modals.openDebugDashboard({
    screenWidth: screen.width,
    screenHeight: screen.height,
    getSnapshot: () => boot.snapshot(),
    onOpenTab: (tab) => boot.openTab(tab),
    onAddGold: (n) => cheats.addGold(n),
    onAddDiamond: (n) => cheats.addDiamond(n),
    onSetChapter: (n) => cheats.setChapter(n),
    onForceDailyReset: () => cheats.forceDailyReset(),
    onGrantAllRelics: () => cheats.grantAllRelics(),
    onChallengeDungeon: () => boot.dungeon.challenge(),
    onSweepDungeon: () => boot.dungeon.sweep(),
    onToggleForceHouseAd: () => cheats.toggleForceHouseAd(),
    forceHouseAdInitial: boot.cheatForceHouseAd(),
    onPreviewHouseAd: (format) => previewHouseAd(boot, format),
    onClose: () => modals.closeDebugDashboard(),
  });
}
